import {
  EIV_STATUS,
  EXPERT_STATUS_ACCOUNT_CREATION,
  RESEAL_ALLOWED_CONTRACTS_IDS,
  STAGE,
} from 'global/constants';

import { getStage } from './StageUtils';

// PRIVATE
const getResealAllowedIds = () => {
  let ids = [];
  switch (getStage()) {
    case STAGE.LOCAL:
      ids = RESEAL_ALLOWED_CONTRACTS_IDS.STAGING;
      break;
    case STAGE.DEVELOPMENT:
      ids = RESEAL_ALLOWED_CONTRACTS_IDS.STAGING;
      break;
    case STAGE.STAGING:
      ids = RESEAL_ALLOWED_CONTRACTS_IDS.UAT;
      break;
    case STAGE.PRODUCTION:
      ids = RESEAL_ALLOWED_CONTRACTS_IDS.PRODUCTION;
      break;
    default:
      ids = [];
      break;
  }
  return ids;
};

/**
 * Capitalizes the first letter of a word.
 *
 * @param {string} word - The word to capitalize.
 * @returns {string} - The capitalized word.
 */
export const branchToCapital = (word) => {
  if (word) {
    word = word.toLowerCase();
    return word.charAt(0).toUpperCase() + word.slice(1);
  }
  return word;
};

/**
 * Converts a boolean value to its German equivalent.
 *
 * @param {boolean} boolValue - The boolean value to convert.
 * @returns {string} - 'Ja' for true, 'Nein' for false.
 */
export const booleanToGerman = (boolValue) => (boolValue ? 'Ja' : 'Nein');

/**
 * Converts a boolean value to 'Ja' or '-' for email representation.
 *
 * @param {boolean} boolValue - The boolean value to convert.
 * @returns {string} - 'Ja' for true, '-' for false.
 */
export const booleanToYesOrHyphen = (boolValue) => (boolValue ? 'Ja' : '-');

/**
 * Converts a possibly empty string to '-'
 *
 * @param {boolean} boolValue - The boolean value to convert.
 * @returns {string} - 'Ja' for true, '-' for false.
 */
export const prepValueForDisplay = (value) => {
  if (!value) return '-';
  return String(value) || '-';
};

export const conditionalChangeEntry = (condition, label, currentValue, previousValue) =>
  condition ? { first: label, second: { first: currentValue, second: previousValue } } : null;

export const conditionalStaticEntry = (condition, label, value) =>
  condition ? { first: label, second: value } : null;

export const getEintragungAlsIdEntries = ({ energyId, gasId, hasEnergyBranch, hasGasBranch }) =>
  [
    conditionalStaticEntry(hasEnergyBranch, 'b_eintragung_als_id', String(energyId)),
    conditionalStaticEntry(hasGasBranch, 'b_eintragung_gas_als_id', String(gasId)),
  ].filter(Boolean);
/**
 * Prepares a value for email, converting booleans to 'Ja'/'Nein' and empty values to '-'.
 *
 * @param {*} value - The value to prepare.
 * @returns {*} - The prepared value.
 */
export const prepValueForEmail = (value, key, comboBoxOptions) => {
  if (typeof value === 'boolean') {
    return booleanToGerman(value);
  }
  // TODO: REMOVE THIS AFTER DASHBOARD IMPLEMENTATION AS EIV WANTS THE IDS
  let returnValue = value;
  const COMBO_BOX_KEYS_TO_TITLE = ['gridProviderEnergy', 'gridProviderGas', 'craftsChamberArea'];
  if (COMBO_BOX_KEYS_TO_TITLE.includes(key)) {
    const comboBoxOption = comboBoxOptions[key].find(
      (option) => String(option.value) === String(value)
    );
    returnValue = comboBoxOption ? comboBoxOption.title : value;
  }
  return prepValueForDisplay(returnValue);
};

/**
 * Prepares a value for any form initial data, converting missing values to default values: [], ""
 *
 * @param {*} value - The value to prepare.
 * @returns {*} - The prepared value.
 */
export const prepValueForInitialData = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value : [];
  }
  if (typeof value === 'number') {
    return value;
  }
  return value || '';
};

/**
 * Parses meter installation codes to their German equivalent.
 *
 * @param {string} meterInstallation - The meter installation code.
 * @returns {string} - 'Ja' for '01' or '02', 'Nein' otherwise.
 */
export const parseMeterInstallation = (meterInstallation) =>
  meterInstallation === '01' || meterInstallation === '02' ? 'Ja' : 'Nein';

/**
 * Determines if resealing is allowed based on contract data.
 *
 * @param {Array} contracts - The list of contracts to check.
 * @returns {string} - 'Ja' if resealing is allowed, 'Nein' otherwise.
 */
export const canReseal = (contracts) => {
  let active = 'Nein';
  contracts.forEach((contract) => {
    if (
      getResealAllowedIds().includes(contract.vertragsart.id) &&
      contract.vertragsstatus === 'Aktiv'
    ) {
      active = 'Ja';
    }
  });
  return active;
};

const isExpertActive = (expert) => {
  return expert.statusGas === EIV_STATUS.ACTIVE || expert.statusEnergy === EIV_STATUS.ACTIVE;
};
const isExpertBranchInactive = (status) => {
  const inactiveStatuses = [EIV_STATUS.INACTIVE, EIV_STATUS.ARCHIVED, EIV_STATUS.DELETED];
  return inactiveStatuses.includes(status);
};
const isExpertBranchInProgress = (status) => {
  return status === EIV_STATUS.IN_PROGRESS;
};
export const canCreateAccount = (expert) => {
  // if at least one is active then it can create accounts without problems
  if (isExpertActive(expert)) {
    return null;
  }
  // when both are inactive then it can't create accounts
  if (isExpertBranchInactive(expert.statusGas) && isExpertBranchInactive(expert.statusEnergy)) {
    return EXPERT_STATUS_ACCOUNT_CREATION.INACTIVE;
  }
  // when at least one is in progress then it can't create accounts, we don't check for mixed statuses as
  // having just one active will not take you here, and having one in progress is enough to get the in progress message
  if (isExpertBranchInProgress(expert.statusGas) || isExpertBranchInProgress(expert.statusEnergy)) {
    return EXPERT_STATUS_ACCOUNT_CREATION.IN_PROGRESS;
  }
  return EXPERT_STATUS_ACCOUNT_CREATION.INACTIVE;
};
